import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { PropertyService } from '../properties/service/property.service';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { CalendarModule } from 'primeng/calendar';
import { TemplateService } from '../templates/templates.service';
import { eventConditionsToDropdownOptions, eventTypesToDropdownOptions, replyTypesToDropdownOptions, templateScopesToDropdownOptions } from '../../helpers/enum-helper';
import { EventType, EventTypeCondition, ReplyType, TemplateScope } from '../../enums/event-type.enum';
import { SendToType } from '../../enums/send-to-type.enum';
import { atLeastOneCheckboxValidator } from '../../core/shared/validators/at-least-one-checkbox.validator';

@Component({
  selector: 'app-create-template',
  standalone: true,
  imports: [
    DialogModule,
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    ButtonModule,
    TableModule,
    ToastModule,
    CalendarModule,
    FormsModule
  ],
  providers: [MessageService],
  templateUrl: './create-template.component.html',
  styleUrls: ['./create-template.component.scss'],
})


export class CreateTemplateComponent {
  @Input() templateData: any | null = null;
  @Input() buttonLabel: any | null = null;
  @Input() sendTo: any | null = null;
  @Output() closeDialog = new EventEmitter<void>();
  @ViewChild('messageTextarea') messageTextarea!: ElementRef;
  @ViewChild('emailTextarea') emailTextarea!: ElementRef;
  templateForm: FormGroup;
  templatedata: any;
  userInfo: any = null;
  eventTypes: { label: string; value: string }[] = [];
  allEventConditions: { label: string; value: string }[] = [];
  eventConditions: { label: string; value: string }[] = [];
  SendToType = SendToType;
  get isExternalLead(): boolean { return this.sendTo === SendToType.EXTERNAL_LEAD; }
  showMore = false;
  activeField: 'content' | 'emailContent' = 'content';
  templateScopes: { label: string; value: string }[] = templateScopesToDropdownOptions(TemplateScope);
  replyTypes: { label: string; value: string }[] = replyTypesToDropdownOptions(ReplyType);
  autoCancelNotificationOptions = [
    { label: 'Notify Residents', value: true },
    { label: 'Do Not Notify Residents', value: false },
  ];
  constructor
    (
      private fb: FormBuilder,
      private propertyService: PropertyService,
      private messageService: MessageService,
      private templateService: TemplateService
    ) {
    this.templateForm = this.fb.group({
      sortId: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      eventType: [null, Validators.required],
      sendTimeCondition: [null, Validators.required],
      sendTime: [0, [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)]],
      content: ['', Validators.required],
      title: ['', Validators.required],
      subject: ['', Validators.required],
      sendAsSms: [true],
      sendAsEmail: [true],
      emailContent: [''],
      scope: [null, Validators.required],
      replyType: [''],
      status: [true],
    }, {
      validators: atLeastOneCheckboxValidator(['sendAsSms', 'sendAsEmail'])
    });
  }

  ngOnInit(): void {
    this.eventTypes = eventTypesToDropdownOptions(EventType, this.sendTo);
    this.allEventConditions = eventConditionsToDropdownOptions(EventTypeCondition, this.sendTo);

    if (this.templateData) {
      this.templateForm.patchValue({
        sortId: this.templateData.sortId,
        content: this.templateData.content,
        eventType: this.templateData.eventType,
        sendTimeCondition: this.templateData.sendTimeCondition,
        sendTime: this.templateData.sendTime,
        sendTo: this.templateData.sendTo,
        title: this.templateData.title,
        subject: this.templateData.subject,
        sendAsSms: this.templateData.sendAsSms,
        sendAsEmail: this.templateData.sendAsEmail,
        emailContent: this.templateData.emailContent,
        scope: this.templateData.scope ?? TemplateScope.GENERAL,
        replyType: this.templateData.replyType ?? ReplyType.EMAIL,
        status: this.templateData.status,
      });
    }

    this.propertyService.getUserInfo().subscribe(user => {
      this.userInfo = user;
    });

    if (!this.templateData) {
      this.getLastSortOrder();
    }

    this.eventConditions = [...this.allEventConditions];

    // For external-lead (No Showing) tab: lock eventType to pulled + scope to general
    if (this.isExternalLead) {
      this.templateForm.get('eventType')?.setValue(EventType.PULLED, { emitEvent: false });
      this.templateForm.get('eventType')?.disable({ emitEvent: false });
      this.templateForm.get('scope')?.setValue(TemplateScope.GENERAL, { emitEvent: false });
      this.templateForm.get('scope')?.disable({ emitEvent: false });
    }

    const initialEventType = this.templateForm.get('eventType')?.value;
    this.updateEventConditions(initialEventType);

    this.templateForm.get('eventType')?.valueChanges.subscribe((eventType: string) => {
      this.updateEventConditions(eventType);
      this.updateScopeAvailability(eventType);
      this.emailResponderValidation(eventType);
      this.autoCancelValidation(eventType);
    });

    this.updateSendTimeField(this.templateForm.get('sendTimeCondition')?.value);
    this.templateForm.get('sendTimeCondition')?.valueChanges.subscribe((condition: string) => {
      this.updateSendTimeField(condition);
    });

    this.templateForm.get('sendAsEmail')?.valueChanges.subscribe((sendAsEmail: boolean) => {
      const emailContentControl = this.templateForm.get('emailContent');

      if (sendAsEmail) {
        emailContentControl?.setValidators([Validators.required]);
      } else {
        emailContentControl?.clearValidators();
      }

      emailContentControl?.updateValueAndValidity();
    });

    this.templateForm.get('status')?.valueChanges.subscribe((notify: boolean) => {
      if (this.templateForm.get('eventType')?.value !== EventType.AUTOCANCEL) return;
      this.applyAutoCancelNotifyBehavior(notify);
    });

    this.updateScopeAvailability(initialEventType);
    this.autoCancelValidation(initialEventType);
  }

  onSubmit(): void {
    if (this.templateForm.valid) {
      const templatePayload = this.templateForm.getRawValue();
      templatePayload.sendTo = this.sendTo;
      // Normalize empty replyType — @IsOptional() only skips null/undefined, not ''
      if (!templatePayload.replyType) {
        templatePayload.replyType = null;
      }
      if (templatePayload.sendTimeCondition == "immediate") {
        templatePayload.sendTime = 0;
      }
      if(templatePayload.content == "" && EventType.EMAILRESPONDER == templatePayload.eventType){
        templatePayload.content = templatePayload.emailContent;
        templatePayload.sendAsSms = false;
      }
      if (templatePayload.eventType === EventType.AUTOCANCEL && !templatePayload.status) {
        templatePayload.sendAsSms = false;
        templatePayload.sendAsEmail = false;
      }
      
      if (this.templateData) {
        this.updateTemplate(templatePayload);
      } else {
        this.createTemplate(templatePayload);
      }
    }
    else {
      console.error('Form is invalid');
    }
  }

  createTemplate(templatePayload: any): void {
    this.templateService.createTemplate(templatePayload).subscribe(
      (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Template created successfully!',
        });
        setTimeout(() => {
          this.closeDialog.emit();
        }, 1500);
      },
      (error) => {
        const errorMessage = error?.error?.message || 'Failed to create template!';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });
      }
    );
  }

  updateTemplate(templatePayload: any): void {
    if (this.templateData && this.templateData._id) {
      this.templateService.updateTemplate(this.templateData._id, templatePayload).subscribe(
        (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Template updated successfully!',
          });
          setTimeout(() => {
            this.closeDialog.emit();
          }, 1500);
        },
        (error) => {
          const errorMessage = error?.error?.message || 'Failed to update template!';
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });
        }
      );
    }
  }

  getLastSortOrder() {
    this.templateService.getLastSortOrder(this.sendTo).subscribe((lastSortId: number) => {
      const nextSortId = (lastSortId || 0) + 1;
      this.templateForm.get('sortId')?.setValue(nextSortId);
    });
  }

  cancelAddTemplate(): void {
    this.closeDialog.emit();
  }

  private static readonly ALL_VARIABLES: { label: string, key: string }[] = [
    { label: 'Lead First Name', key: 'lead_first_name' },
    { label: 'Lead Last Name', key: 'lead_last_name' },
    { label: 'Resident First Name', key: 'resident_first_name' },
    { label: 'Resident Last Name', key: 'resident_last_name' },
    { label: 'Property Name', key: 'property_name' },
    { label: 'Property Address', key: 'property_address' },
    { label: 'Property Phone Number', key: 'property_phone_number' },
    { label: 'Property Email Address', key: 'property_email' },
    { label: 'Lockbox Code', key: 'lockbox_code' },
    { label: 'Tour Date', key: 'schedule_date' },
    { label: 'Tour Time', key: 'schedule_time' },
    { label: 'Unit Name', key: 'plan_name' },
    { label: 'Floorplan Name', key: 'floorplan_name' },
    { label: 'Application Link', key: 'application_link' },
    { label: 'One Hour Before Tour', key: 'one_hour_before' },
    { label: 'One Hour After Tour', key: 'one_hour_after' },
    { label: 'Accept Tour', key: 'accept_tour' },
    { label: 'Old Date', key: 'old_tour_date' },
    { label: 'Old Time', key: 'old_tour_time' },
    { label: 'Tour Schedule Link', key: 'tour_schedule_link' },
    { label: 'Tour ID', key: 'tour_id' },
    { label: 'Lead Source', key: 'lead_source' },
  ];

  private static readonly EXTERNAL_LEAD_EXCLUDED_VARS = new Set([
    'resident_name', 'resident_first_name', 'resident_last_name', 'resident_phone_number',
    'schedule_date', 'schedule_time', 'lockbox_code', 'application_link',
    'one_hour_before', 'one_hour_after', 'accept_tour',
    'old_tour_date', 'old_tour_time', 'tour_id', 'tour_no',
    'reschedule_link_url', 'reschedule_link_anchor',
  ]);

  get variables(): { label: string, key: string }[] {
    if (!this.isExternalLead) return CreateTemplateComponent.ALL_VARIABLES;
    return CreateTemplateComponent.ALL_VARIABLES.filter(
      v => !CreateTemplateComponent.EXTERNAL_LEAD_EXCLUDED_VARS.has(v.key)
    );
  }


  toggleShowMore(): void {
    this.showMore = !this.showMore;
  }

  setActiveField(field: 'content' | 'emailContent') {
    this.activeField = field;
  }

  insertVariable(variable: string): void {
    const target: 'content' | 'emailContent' = this.activeField || 'content';
    const textarea = target === 'content' ? this.messageTextarea.nativeElement : this.emailTextarea.nativeElement;
    if (!textarea) {
      console.error(`Textarea for ${target} not found`);
      return;
    }
    const { selectionStart, selectionEnd, value } = textarea;
    const variableText = `{${variable}}`;

    const newValue =
      value.substring(0, selectionStart) +
      variableText +
      value.substring(selectionEnd);

    this.templateForm.get(target)?.setValue(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        selectionStart + variableText.length,
        selectionStart + variableText.length
      );
    }, 0);
  }

  getVariableDisplay(variable: { label: string, key: string }) {
    return `${variable.label}: {${variable.key}}`;
  }

  private updateEventConditions(eventType: string) {
    if (eventType === EventType.PULLED) {
      // No Showing: only Immediate and No Response (already filtered in allEventConditions via sendTo)
      this.eventConditions = [...this.allEventConditions];
      const currentValue = this.templateForm.get('sendTimeCondition')?.value;
      if (!['immediate', 'no_response'].includes(currentValue)) {
        this.templateForm.get('sendTimeCondition')?.setValue('immediate');
      }
    } else if ([EventType.CREATED, EventType.ACCEPTED, EventType.ALREADYBOOKED, EventType.RESCHEDULED, EventType.EMAILRESPONDER, EventType.AUTOCANCEL].includes(eventType as EventType)) {
      this.eventConditions = this.allEventConditions.filter(
        c => c.value === 'immediate' || c.value === 'after'
      );
      const currentValue = this.templateForm.get('sendTimeCondition')?.value;
      if (!['immediate', 'after'].includes(currentValue)) {
        this.templateForm.get('sendTimeCondition')?.setValue('immediate');
      }
    } else {
      this.eventConditions = [...this.allEventConditions];
    }
  }

  private updateSendTimeField(condition: string) {
    const sendTimeControl = this.templateForm.get('sendTime');
    if (!sendTimeControl) return;

    if (condition === 'immediate') {
      sendTimeControl.setValue(0, { emitEvent: false });
      sendTimeControl.disable({ emitEvent: false });
    } else {
      // 'before', 'after', or 'no_response' — all need a sendTime value
      if (!sendTimeControl.value || sendTimeControl.value === 0) {
        sendTimeControl.setValue(24, { emitEvent: false });
      }
      sendTimeControl.enable({ emitEvent: false });
    }
  }

  private updateScopeAvailability(eventType: string): void {
    const scopeControl = this.templateForm.get('scope');
    if (!scopeControl) return;

    if (eventType === EventType.AUTOCANCEL) {
      scopeControl.setValue(TemplateScope.GENERAL);
      scopeControl.disable({ emitEvent: false });
      return;
    }

    if (this.templateData?.scope || eventType === EventType.SCHEDULED) {
      scopeControl.enable({ emitEvent: false });
      return;
    }

    scopeControl.setValue(TemplateScope.GENERAL);
    scopeControl.enable({ emitEvent: false });
  }

  private emailResponderValidation(eventType: string) {
      const contentControl = this.templateForm.get('content');
      if (eventType === EventType.EMAILRESPONDER) {
        // Disable form-level validator
        this.templateForm.setValidators(null);

        // Disable content validation
        contentControl?.clearValidators();

        // Reset and disable checkboxes
        this.templateForm.get('sendAsSms')?.setValue(false);
        this.templateForm.get('sendAsEmail')?.setValue(true); // you can force email true if needed
        this.templateForm.get('sendAsSms')?.disable();
        this.templateForm.get('sendAsEmail')?.disable();
        this.templateForm.get('replyType')?.setValue(this.replyTypes[0].value);
      } else {
        // Re-enable form-level validator
        this.templateForm.setValidators(atLeastOneCheckboxValidator(['sendAsSms', 'sendAsEmail']));

        // Re-enable content validation
        contentControl?.setValidators([Validators.required]);

        // Enable checkboxes
        this.templateForm.get('sendAsSms')?.enable();
        this.templateForm.get('sendAsEmail')?.enable();
        this.templateForm.get('replyType')?.setValue(null);
        this.templateForm.get('sendAsSms')?.setValue(true);
        this.templateForm.get('sendAsEmail')?.setValue(true);
      }

      // Update validity after changing validators
      this.templateForm.updateValueAndValidity();
      contentControl?.updateValueAndValidity();
  }

  private autoCancelValidation(eventType: string) {
    const sendTimeConditionControl = this.templateForm.get('sendTimeCondition');
    const sendTimeControl = this.templateForm.get('sendTime');
    if (!sendTimeConditionControl || !sendTimeControl) return;

    if (eventType === EventType.AUTOCANCEL) {
      sendTimeConditionControl.setValue(EventTypeCondition.IMMEDIATE, { emitEvent: false });
      sendTimeConditionControl.disable({ emitEvent: false });
      sendTimeControl.setValue(0, { emitEvent: false });
      sendTimeControl.disable({ emitEvent: false });
      this.applyAutoCancelNotifyBehavior(this.templateForm.get('status')?.value);
      return;
    }

    sendTimeConditionControl.enable({ emitEvent: false });
    this.updateSendTimeField(sendTimeConditionControl.value);
    this.templateForm.get('sendAsSms')?.enable({ emitEvent: false });
    this.templateForm.get('sendAsEmail')?.enable({ emitEvent: false });
  }

  private applyAutoCancelNotifyBehavior(notifyResidents: boolean) {
    const sendAsSmsControl = this.templateForm.get('sendAsSms');
    const sendAsEmailControl = this.templateForm.get('sendAsEmail');
    if (!sendAsSmsControl || !sendAsEmailControl) return;

    // if (!notifyResidents) {
    //   sendAsSmsControl.setValue(false, { emitEvent: false });
    //   sendAsEmailControl.setValue(false, { emitEvent: false });
    //   sendAsSmsControl.disable({ emitEvent: false });
    //   sendAsEmailControl.disable({ emitEvent: false });
    //   return;
    // }

    sendAsSmsControl.enable({ emitEvent: false });
    sendAsEmailControl.enable({ emitEvent: false });

    if (!sendAsSmsControl.value && !sendAsEmailControl.value) {
      sendAsSmsControl.setValue(true, { emitEvent: false });
      sendAsEmailControl.setValue(true, { emitEvent: false });
    }
  }
}
